import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Check } from "lucide-react";
import { useLang } from "@/contexts/LangContext";
import Reveal from "@/components/Reveal";
import GoldCorners from "@/components/GoldCorners";

const SIGNATURE =
  "https://customer-assets.emergentagent.com/job_web-essentials-21/artifacts/nvakaqkb_IMG_2114.png";

// Featured programs — chef plating + healthy meal composition (dark chiaroscuro)
const FEATURED_IMGS = {
  "private-culinary":
    "https://customer-assets.emergentagent.com/job_web-essentials-21/artifacts/hzqinar3_IMG_2099.jpeg",
  "nutrition-program":
    "https://customer-assets.emergentagent.com/job_web-essentials-21/artifacts/x8bsamln_IMG_2103.png",
};

function CinematicPhoto({ src, alt }) {
  return (
    <div
      className="relative aspect-[4/5] sm:aspect-[16/11] lg:aspect-[4/5] overflow-hidden"
      style={{ boxShadow: "0 40px 80px -20px rgba(18, 60, 90, 0.65), 0 20px 40px -10px rgba(7, 14, 19, 0.7)" }}
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="absolute inset-0 w-full h-full object-cover"
        style={{ filter: "brightness(0.82) contrast(1.1) saturate(1.05)" }}
      />
      <div className="absolute inset-0 mix-blend-overlay opacity-55" style={{ background: "linear-gradient(135deg, rgba(199,162,108,0.28) 0%, rgba(168,132,90,0.18) 45%, rgba(7,14,19,0.55) 100%)" }} />
      <div className="absolute inset-0 bg-gradient-to-t from-[#070E13]/80 via-[#070E13]/20 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#070E13]/40 via-transparent to-transparent" />
      <div className="absolute inset-0 pointer-events-none" style={{ boxShadow: "inset 0 0 160px 30px rgba(7,14,19,0.6), inset 0 0 70px 10px rgba(199,162,108,0.10)" }} />
      <GoldCorners size={16} inset={-2} />
    </div>
  );
}

export default function Services() {
  const { t } = useLang();

  return (
    <div data-testid="page-services" className="bg-[#070E13] text-[#F4EEE8]">
      {/* HERO with signature image right-aligned */}
      <section className="relative min-h-[70vh] overflow-hidden bg-[#070E13]">
        <div className="absolute inset-0 lg:inset-y-0 lg:right-0 lg:left-[42%]">
          <img src={SIGNATURE} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#070E13] from-0% via-[#070E13]/80 via-25% to-transparent to-55%" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#070E13]/35 via-transparent to-[#070E13]/55" />
          <div className="absolute inset-0 mix-blend-overlay opacity-25" style={{ background: "linear-gradient(135deg, rgba(199,162,108,0.20), rgba(7,14,19,0.4))" }} />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 lg:px-12 pt-40 pb-20">
          <div className="lg:w-[55%]">
            <Reveal>
              <p className="overline gold-text">{t.services.eyebrow}</p>
              <h1 className="h-editorial text-5xl sm:text-6xl lg:text-7xl mt-6 max-w-4xl">{t.services.title}</h1>
              <p className="mt-8 max-w-2xl text-lg text-[#D6D1C9]">{t.services.lead}</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FEATURED SIGNATURE PROGRAMS — 2 flagship offerings with alternating cinematic layout */}
      <section data-testid="services-featured" className="bg-[#070E13] py-24 sm:py-32 border-t border-[#C7A26C]/15">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12">
          <Reveal>
            <div className="max-w-3xl">
              <p className="overline gold-text tracking-[0.28em]">{t.services.featuredEyebrow}</p>
              <h2 className="h-editorial text-4xl sm:text-5xl mt-5 leading-[1.1]">{t.services.featuredTitle}</h2>
            </div>
          </Reveal>

          <div className="mt-20 sm:mt-24 space-y-28 sm:space-y-36">
            {t.services.featured.map((f, idx) => {
              const reverse = idx % 2 === 1;
              return (
                <div
                  key={f.slug}
                  data-testid={`featured-${f.slug}`}
                  className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start"
                >
                  {/* Photo — sticky on desktop so it stays while reading long content */}
                  <Reveal className={`lg:col-span-5 lg:sticky lg:top-28 ${reverse ? "lg:order-2" : ""}`}>
                    <CinematicPhoto src={FEATURED_IMGS[f.slug]} alt={f.title} />
                  </Reveal>

                  {/* Content */}
                  <Reveal delay={150} className={`lg:col-span-7 ${reverse ? "lg:order-1 lg:pe-6" : "lg:ps-6"}`}>
                    <p className="overline gold-text tracking-[0.24em]">{f.eyebrow}</p>
                    <h3 className="h-editorial text-3xl sm:text-4xl lg:text-5xl mt-5 leading-[1.1]">{f.title}</h3>
                    <p className="mt-5 font-serif italic text-xl text-[#C7A26C]/90">{f.subtitle}</p>
                    <p className="mt-6 text-[#D6D1C9] leading-relaxed max-w-2xl">{f.lead}</p>
                    <div className="mt-7 border-s-2 border-[#C7A26C]/60 ps-5">
                      <p className="font-serif italic text-lg text-[#F4EEE8]/90 leading-relaxed">{f.promise}</p>
                    </div>

                    {/* Pillars */}
                    <div className="mt-10">
                      <p className="overline text-[#C7A26C]/80">{f.pillarsTitle}</p>
                      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
                        {f.pillars.map((p, i) => (
                          <div key={i} className="relative bg-[#0C1821] border border-[#C7A26C]/20 p-6">
                            <GoldCorners size={10} inset={-1} />
                            <p className="font-serif text-3xl gold-text">{p.n}</p>
                            <p className="font-serif text-lg mt-3">{p.t}</p>
                            <p className="mt-2 text-xs text-[#D6D1C9] leading-relaxed">{p.d}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Includes */}
                    <div className="mt-10">
                      <p className="overline text-[#C7A26C]/80">{f.includesTitle}</p>
                      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-8">
                        {f.includes.map((col, i) => (
                          <div key={i}>
                            <p className="font-serif text-lg text-[#C7A26C]">{col.label}</p>
                            <ul className="mt-4 space-y-3">
                              {col.items.map((it, j) => (
                                <li key={j} className="flex gap-3 items-start text-sm text-[#D6D1C9]">
                                  <Check size={14} className="text-[#C7A26C] mt-1 shrink-0" strokeWidth={1.8} />
                                  <span className="leading-relaxed">{it}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Process */}
                    <div className="mt-10">
                      <p className="overline text-[#C7A26C]/80">{f.processTitle}</p>
                      <div className="mt-6 space-y-5">
                        {f.process.map((p, i) => (
                          <div key={i} className="flex gap-5 items-start">
                            <span className="font-serif text-2xl gold-text shrink-0 min-w-[3rem]">{p.n}</span>
                            <div>
                              <p className="font-serif text-lg text-[#F4EEE8]">{p.t}</p>
                              <p className="mt-1 text-sm text-[#D6D1C9] leading-relaxed">{p.d}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Chef training / Track / Follow-up */}
                    <div className="mt-10 bg-[#0C1821] p-7 lg:p-9 border border-[#C7A26C]/25 relative">
                      <GoldCorners size={12} inset={-1} />
                      <p className="font-serif text-2xl text-[#F4EEE8]">{f.chefTrainingTitle}</p>
                      <p className="mt-3 text-sm text-[#D6D1C9] leading-relaxed">{f.chefTrainingLead}</p>
                      <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                        {f.curriculum.map((c, i) => (
                          <li key={i} className="flex gap-2 items-start text-xs text-[#D6D1C9]">
                            <span className="text-[#C7A26C] mt-0.5 shrink-0">—</span>
                            <span className="leading-relaxed">{c}</span>
                          </li>
                        ))}
                      </ul>
                      <p className="mt-6 overline text-[#C7A26C]">{f.outcome}</p>
                    </div>

                    {/* Closing */}
                    <div className="mt-10">
                      <h4 className="font-serif text-2xl sm:text-3xl text-[#F4EEE8] leading-snug">{f.closingTitle}</h4>
                      <p className="mt-5 text-[#D6D1C9] leading-relaxed max-w-2xl">{f.closing}</p>
                    </div>

                    <Link
                      to="/contact"
                      data-testid={`featured-cta-${f.slug}`}
                      className="cta-premium inline-flex items-center gap-2 mt-10 px-9 py-4 bg-gradient-to-br from-[#E0C58E] via-[#C7A26C] to-[#A8845A] text-[#070E13] text-xs uppercase tracking-[0.24em] font-medium"
                    >
                      {t.common.bookConsultation} <ArrowRight size={16} />
                    </Link>
                  </Reveal>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ADDITIONAL DISCIPLINES */}
      <section className="bg-[#070E13] py-20 sm:py-28 border-t border-[#C7A26C]/15">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 lg:px-12">
          <Reveal>
            <p className="overline gold-text">{t.services.moreTitle}</p>
            <h2 className="h-editorial text-3xl sm:text-4xl mt-5 max-w-3xl">{t.services.moreLead}</h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6">
            {t.services.items.map((s, i) => (
              <Reveal
                key={i}
                delay={i * 60}
                className="relative bg-[#0C1821] text-[#F4EEE8] p-10 lg:p-14 group hover:bg-[#14222C] transition-colors duration-500 border border-[#C7A26C]/20 hover:border-[#C7A26C]/55"
              >
                <GoldCorners size={12} inset={-1} />
                <p className="font-serif text-6xl gold-text opacity-80">0{i + 1}</p>
                <h3 className="font-serif text-3xl mt-6">{s.title}</h3>
                <p className="mt-4 text-[#D6D1C9] leading-relaxed text-base">{s.body}</p>
                <Link
                  to="/contact"
                  data-testid={`service-cta-${i}`}
                  className="inline-flex items-center gap-2 mt-8 text-[#C7A26C] link-underline text-xs uppercase tracking-[0.22em]"
                >
                  {t.common.bookConsultation} <ArrowUpRight size={14} />
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#070E13] text-[#F4EEE8] py-24 border-t border-[#C7A26C]/15">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <Reveal>
            <p className="overline gold-text">{t.contact.eyebrow}</p>
            <h2 className="h-editorial text-4xl sm:text-5xl mt-6">{t.contact.title}</h2>
            <Link
              to="/contact"
              data-testid="services-bottom-cta"
              className="cta-premium inline-flex items-center gap-2 mt-10 px-9 py-4 bg-gradient-to-br from-[#E0C58E] via-[#C7A26C] to-[#A8845A] text-[#070E13] text-xs uppercase tracking-[0.24em] font-medium"
            >
              {t.common.bookConsultation}
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
